import Decimal from 'decimal.js';

// Piecewise-exponential slider mapping. The slider position t ∈ [0, 1]
// is split at t=0.5 so that t=0.5 always lands exactly on `mid`. Each
// half is log-linear, which gives the user fine control near the small
// end (where most loans live) and coarse control at the large end.

interface Bounds {
  min: Decimal;
  mid: Decimal;
  max: Decimal;
}

const ZERO = new Decimal(0);
const ONE = new Decimal(1);
const HALF = new Decimal('0.5');

export function sliderToValue(t: Decimal, bounds: Bounds): Decimal {
  let clamped = t;
  if (clamped.lt(ZERO)) clamped = ZERO;
  if (clamped.gt(ONE)) clamped = ONE;
  if (clamped.eq(ZERO)) return bounds.min;
  if (clamped.eq(ONE)) return bounds.max;

  if (clamped.lte(HALF)) {
    // u = clamped / 0.5  →  bounds.min * (bounds.mid / bounds.min) ^ u
    const u = clamped.div(HALF);
    return bounds.min.mul(bounds.mid.div(bounds.min).pow(u));
  }
  // u = (clamped - 0.5) / 0.5  →  bounds.mid * (bounds.max / bounds.mid) ^ u
  const u = clamped.minus(HALF).div(HALF);
  return bounds.mid.mul(bounds.max.div(bounds.mid).pow(u));
}

export function valueToSlider(value: Decimal, bounds: Bounds): number {
  let clamped = value;
  if (clamped.lt(bounds.min)) clamped = bounds.min;
  if (clamped.gt(bounds.max)) clamped = bounds.max;

  if (clamped.lte(bounds.mid)) {
    // 0.5 * ln(v/min) / ln(mid/min)
    const num = clamped.div(bounds.min).ln();
    const den = bounds.mid.div(bounds.min).ln();
    return num.div(den).div(2).toNumber();
  }
  // 0.5 + 0.5 * ln(v/mid) / ln(max/mid)
  const num = clamped.div(bounds.mid).ln();
  const den = bounds.max.div(bounds.mid).ln();
  return num.div(den).div(2).plus(HALF).toNumber();
}
